import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { id: "home", label: "Home", testId: NAV.linkHome },
  { id: "work", label: "Work", testId: NAV.linkWork },
  { id: "services", label: "Services", testId: "nav-link-services" },
  { id: "about", label: "About", testId: NAV.linkAbout },
  { id: "contact", label: "Contact", testId: NAV.linkContact },
];

const smoothScroll = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <button
          data-testid={NAV.logo}
          data-cursor="Home"
          onClick={() => smoothScroll("home")}
          className="flex items-center gap-2 group"
        >
          <span className="inline-block w-2.5 h-2.5 bg-[#E2FF31] rounded-sm rotate-45 group-hover:rotate-[135deg] transition-transform duration-500" />
          <span className="font-display text-base lg:text-lg font-semibold tracking-tight">
            Elevate<span className="text-[#E2FF31]">.</span>studios
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {links.map((link) => (
            <button
              key={link.id}
              data-testid={link.testId}
              data-cursor={link.label}
              onClick={() => smoothScroll(link.id)}
              className="nav-underline text-sm font-mono-tag text-white/70 hover:text-white transition-colors"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <button
          data-testid={NAV.ctaTalk}
          data-cursor="Let's talk"
          onClick={() => smoothScroll("contact")}
          className="hidden md:inline-flex btn-primary text-sm"
        >
          Start a project
          <span className="arrow">→</span>
        </button>

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 space-y-5">
          {links.map((link) => (
            <button
              key={link.id}
              data-testid={`${link.testId}-mobile`}
              onClick={() => {
                smoothScroll(link.id);
                setOpen(false);
              }}
              className="block w-full text-left font-display text-2xl font-medium"
            >
              {link.label}
            </button>
          ))}
          <button
            data-testid={`${NAV.ctaTalk}-mobile`}
            onClick={() => {
              smoothScroll("contact");
              setOpen(false);
            }}
            className="btn-primary w-full justify-center"
          >
            Start a project <span className="arrow">→</span>
          </button>
        </div>
      )}
    </header>
  );
}
